import { useAppDispatch, useAppSelector } from "./redux";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  homeHealthController,
  localStorageProvider,
  notificationController,
  patientController,
  smsController,
  summaryController,
  timeProvider,
  userController,
  watchlistController,
} from "../AppController";
import { useRouterHook } from "./useRouterHook";
import { appNotification } from "../util/appNotification";
import { isPIN } from "../util/pin";
import { setDashboardOutdateTabsAll } from "../redux/Dashboard/dashboard.slice";
import { useAppNavigate } from "./router";
import { getDefaultLandingPath } from "../util/defaultLandingPath";
import { CIP_2_0_UI_FEATURE, getBetaFeatures } from "../util/betaFeatures";
import { isInternalLaunchOverride } from "../util/subdomain";

/**
 * App-wide facility switch: persists the selection (with the user's
 * notification preferences), then reboots the app state — in-place via
 * ``populateAppData`` on the Circadia Android shell, via a full page reload
 * everywhere else.
 *
 * Extracted from ``HeaderWeb`` so surfaces that hide the global header's
 * selector (e.g. Neo's Copilot landing) can offer the same behavior.
 */
export const useChangeFacility = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { clinicList, notificationSettings, user } = useAppSelector(
    (state) => ({
      clinicList: state.clinic.clinicList,
      notificationSettings: state.settings.notifications,
      user: state.auth.user,
    })
  );

  const { populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    watchlistController: watchlistController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider: circadiaEngineProvider,
    summaryController: summaryController,
    homeHealthController: homeHealthController,
    isPIN: isPIN,
    smsController: smsController,
    userController: userController,
  });

  const changeFacility = async (clinicId: number) => {
    const selectedClinic = await clinicController.setSelectedClinic({
      id: clinicId,
      clinicList,
      notificationSettings: {
        email: notificationSettings?.email || false,
        pushNotification: notificationSettings?.pushNotification || false,
        kioskMode: notificationSettings?.kioskMode || false,
        escalatedAlertPreference:
          notificationSettings?.escalatedAlertPreference || [],
        facilitiesToSendNotificationForOldId: [clinicId],
        psnAlertPreference: notificationSettings?.psnAlertPreference || [],
        bedExitPreference: notificationSettings?.bedExitPreference || [],
        sms: notificationSettings?.sms || false,
        smsPreferences: notificationSettings?.smsPreferences || {
          morning_report: false,
          afternoon_report: false,
          evening_report: false,
          just_in_time: false,
        },
      },
    });
    if (selectedClinic) {
      localStorageProvider.remove("dashboard_filters");
      // The 2026 landing (Predict) depends on the TARGET clinic, which only
      // loads with populateAppData. Resolve the landing after that load (or after
      // the reload, via the catch-all redirect) for anyone who can see the 2026
      // UI: users with the user-level `cip2.0-ui` feature, AND internal employees
      // on a beta/local host (the internal launch override, which grants the 2026
      // UI without the feature). Everyone else keeps the original flow.
      const awaitTargetFeatures =
        getBetaFeatures().includes(CIP_2_0_UI_FEATURE) ||
        isInternalLaunchOverride(user?.email);
      const landingPath = getDefaultLandingPath(user, selectedClinic);
      const isCircadia = circadiaEngineProvider.isWindowCircadia();
      const isAndroid = circadiaEngineProvider.plat === "android";
      if (isCircadia && isAndroid) {
        dispatch(setDashboardOutdateTabsAll());
        await populateAppData(true);
        navigate(
          awaitTargetFeatures
            ? getDefaultLandingPath(user, selectedClinic)
            : landingPath,
          { replace: true }
        );
      } else {
        // Hash routes survive reloads, so replace the old clinic's route first.
        navigate(awaitTargetFeatures ? "/" : landingPath, { replace: true });
        window.location.reload();
      }
      return true;
    }
    return false;
  };

  return changeFacility;
};
